/**
 * Visual Stage — Stage 4: Visual aid validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.5
 *
 * Validates:
 * - Required families have visual aids
 * - stimulusAids/supportAids follow semantic convention
 * - Visual types match policy-allowed types (multi-type support)
 * - Visual aid types are known (in VisualAidType union)
 * - Visual aid data matches schema (VISUAL_AID_SCHEMAS.json)
 * - No answer leakage in visual data
 */

import type { ValidationIssue, PipelineStage } from '../issueContract'
import { blocker, coach, info } from '../issueContract'
import { getVisualPolicy, familyRequiresVisual } from '../policyEngine'
import type { Question } from '../../../content'
import { VISUAL_AID_TYPES } from '../../../visuals'
import visualAidSchemas from '../../data/VISUAL_AID_SCHEMAS.json'

const STAGE: PipelineStage = 4

/** Set of known visual aid types for O(1) lookup */
const KNOWN_VISUAL_TYPES = new Set<string>(VISUAL_AID_TYPES)

/** Parsed schema definitions for data validation */
const SCHEMA_DEFS = (visualAidSchemas as Record<string, unknown>)
  .definitions as Record<string, Record<string, unknown>> | undefined

/**
 * Basic data schema validation — checks required fields from VISUAL_AID_SCHEMAS.json.
 * Returns an array of missing required field names.
 */
function validateVisualDataSchema(
  type: string,
  data: Record<string, unknown>,
): string[] {
  if (!SCHEMA_DEFS) return []
  const schema = SCHEMA_DEFS[type]
  if (!schema) return [] // No schema defined for this type

  const required = schema.required as string[] | undefined
  if (!required || required.length === 0) return []

  const missing: string[] = []
  for (const field of required) {
    if (!(field in data) || data[field] === undefined || data[field] === null) {
      missing.push(field)
    }
  }
  return missing
}

/**
 * Run visual validation on a question.
 */
export function runVisualStage(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  if (!question.familyId) return issues

  const visualPolicy = getVisualPolicy(question.familyId)
  const isRequired = familyRequiresVisual(question.familyId)

  // ── Unknown visual type + data schema validation (all questions) ──────

  const allAids: Array<{
    aid: Record<string, unknown>
    role: string
    idx: number
  }> = []

  if (Array.isArray(question.stimulusAids)) {
    for (let i = 0; i < question.stimulusAids.length; i++) {
      const aid = question.stimulusAids[i] as
        | Record<string, unknown>
        | undefined
      if (aid && typeof aid === 'object') {
        allAids.push({ aid, role: 'stimulusAids', idx: i })
      }
    }
  }
  if (Array.isArray(question.supportAids)) {
    for (let i = 0; i < question.supportAids.length; i++) {
      const aid = question.supportAids[i] as Record<string, unknown> | undefined
      if (aid && typeof aid === 'object') {
        allAids.push({ aid, role: 'supportAids', idx: i })
      }
    }
  }

  for (const { aid, role, idx } of allAids) {
    const aidType = aid.type as string | undefined
    if (aidType && typeof aidType === 'string') {
      // Check unknown type
      if (!KNOWN_VISUAL_TYPES.has(aidType)) {
        issues.push(
          blocker(
            'visual',
            STAGE,
            'VISUAL_UNKNOWN_AID_TYPE',
            `Visual aid type "${aidType}" is not a known VisualAidType`,
            {
              field: `${role}[${idx}].type`,
              actual: aidType,
              questionId: qId,
              familyId: fId,
              suggestion: `Valid types: ${VISUAL_AID_TYPES.join(', ')}`,
            },
          ),
        )
      }

      // Check data schema
      const data = aid.data as Record<string, unknown> | undefined
      if (data && typeof data === 'object') {
        const missingFields = validateVisualDataSchema(aidType, data)
        for (const field of missingFields) {
          issues.push(
            coach(
              'visual',
              STAGE,
              'VISUAL_DATA_SCHEMA_VIOLATION',
              `Visual aid "${aidType}" is missing required data field "${field}"`,
              {
                field: `${role}[${idx}].data.${field}`,
                questionId: qId,
                familyId: fId,
                severity: 50,
                suggestion: `Add "${field}" to the ${aidType} visual data object`,
              },
            ),
          )
        }
      }
    }
  }

  // ── Required visual check ────────────────────────────────────────────────

  if (isRequired && visualPolicy) {
    // Stimulus required check
    if (visualPolicy.stimulusRequired) {
      const hasStimulus =
        Array.isArray(question.stimulusAids) && question.stimulusAids.length > 0

      // Exception: ordering-type questions within stimulus-required families
      const isOrderingException =
        question.questionType === 'ordering' && !hasStimulus

      if (!hasStimulus && !isOrderingException) {
        issues.push(
          blocker(
            'visual',
            STAGE,
            'VISUAL_STIMULUS_REQUIRED',
            `Family "${question.familyId}" requires a stimulus visual aid`,
            {
              field: 'stimulusAids',
              questionId: qId,
              familyId: fId,
              suggestion: visualPolicy.stimulusDefault
                ? `Add a "${visualPolicy.stimulusDefault}" visual to stimulusAids`
                : 'Add an appropriate visual to stimulusAids',
            },
          ),
        )
      } else if (
        hasStimulus &&
        visualPolicy.stimulusAllowed &&
        visualPolicy.stimulusAllowed.length > 0
      ) {
        // Check stimulus type against allowed list (multi-type support)
        const allowedSet = new Set<string>(visualPolicy.stimulusAllowed)
        for (let i = 0; i < question.stimulusAids.length; i++) {
          const aid = question.stimulusAids[i] as
            | Record<string, unknown>
            | undefined
          if (aid && typeof aid === 'object' && 'type' in aid) {
            const aidType = aid.type as string
            if (!allowedSet.has(aidType)) {
              issues.push(
                coach(
                  'visual',
                  STAGE,
                  'VISUAL_STIMULUS_TYPE_MISMATCH',
                  `Stimulus visual type "${aidType}" is not in the allowed types [${visualPolicy.stimulusAllowed.join(', ')}] for family "${question.familyId}"`,
                  {
                    field: `stimulusAids[${i}].type`,
                    expected: visualPolicy.stimulusAllowed.join(', '),
                    actual: aidType,
                    questionId: qId,
                    familyId: fId,
                    severity: 35,
                    suggestion: `Consider using one of [${visualPolicy.stimulusAllowed.join(', ')}], or this type may be intentional for this question`,
                  },
                ),
              )
            }
          }
        }
      }
    }

    // Support required check
    if (visualPolicy.supportRequired) {
      const hasSupport =
        Array.isArray(question.supportAids) && question.supportAids.length > 0
      if (!hasSupport) {
        issues.push(
          coach(
            'visual',
            STAGE,
            'VISUAL_SUPPORT_RECOMMENDED',
            `Family "${question.familyId}" should have a support visual aid`,
            {
              field: 'supportAids',
              questionId: qId,
              familyId: fId,
              severity: 50,
              suggestion: visualPolicy.supportDefault
                ? `Add a "${visualPolicy.supportDefault}" visual to supportAids`
                : 'Add an appropriate visual to supportAids',
            },
          ),
        )
      } else if (hasSupport) {
        // Check support type against allowed list (multi-type support)
        if (
          visualPolicy.supportAllowed &&
          visualPolicy.supportAllowed.length > 0
        ) {
          const allowedSet = new Set<string>(visualPolicy.supportAllowed)
          for (let i = 0; i < question.supportAids.length; i++) {
            const aid = question.supportAids[i] as
              | Record<string, unknown>
              | undefined
            if (aid && typeof aid === 'object' && 'type' in aid) {
              const aidType = aid.type as string
              if (!allowedSet.has(aidType)) {
                issues.push(
                  coach(
                    'visual',
                    STAGE,
                    'VISUAL_SUPPORT_TYPE_MISMATCH',
                    `Support visual type "${aidType}" is not in the allowed types [${visualPolicy.supportAllowed.join(', ')}] for family "${question.familyId}"`,
                    {
                      field: `supportAids[${i}].type`,
                      expected: visualPolicy.supportAllowed.join(', '),
                      actual: aidType,
                      questionId: qId,
                      familyId: fId,
                      severity: 30,
                      suggestion: `Consider using one of [${visualPolicy.supportAllowed.join(', ')}], or this type may be intentional`,
                    },
                  ),
                )
              }
            }
          }
        }

        // showWhen validation
        if (visualPolicy.supportShowWhen) {
          for (let i = 0; i < question.supportAids.length; i++) {
            const aid = question.supportAids[i] as
              | Record<string, unknown>
              | undefined
            if (aid && typeof aid === 'object') {
              const showWhen = aid.showWhen as string | undefined
              if (!showWhen) {
                issues.push(
                  info(
                    'visual',
                    STAGE,
                    'VISUAL_SUPPORT_MISSING_SHOW_WHEN',
                    `Support aid at index ${i} is missing showWhen property — policy expects "${visualPolicy.supportShowWhen}"`,
                    {
                      field: `supportAids[${i}].showWhen`,
                      expected: visualPolicy.supportShowWhen,
                      questionId: qId,
                      familyId: fId,
                      severity: 20,
                      suggestion: `Add showWhen: "${visualPolicy.supportShowWhen}" to the support aid`,
                    },
                  ),
                )
              } else if (showWhen !== visualPolicy.supportShowWhen) {
                issues.push(
                  info(
                    'visual',
                    STAGE,
                    'VISUAL_SUPPORT_SHOW_WHEN_MISMATCH',
                    `Support aid showWhen "${showWhen}" does not match policy "${visualPolicy.supportShowWhen}" for family "${question.familyId}"`,
                    {
                      field: `supportAids[${i}].showWhen`,
                      expected: visualPolicy.supportShowWhen,
                      actual: showWhen,
                      questionId: qId,
                      familyId: fId,
                      severity: 15,
                    },
                  ),
                )
              }
            }
          }
        }
      }
    }
  }

  // ── Semantic convention check ────────────────────────────────────────────

  if (
    question.stimulusAids !== undefined &&
    !Array.isArray(question.stimulusAids)
  ) {
    issues.push(
      blocker(
        'visual',
        STAGE,
        'VISUAL_STIMULUS_INVALID_TYPE',
        'stimulusAids must be an array (use [] for reviewed-no-visuals)',
        {
          field: 'stimulusAids',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    question.supportAids !== undefined &&
    !Array.isArray(question.supportAids)
  ) {
    issues.push(
      blocker(
        'visual',
        STAGE,
        'VISUAL_SUPPORT_INVALID_TYPE',
        'supportAids must be an array (use [] for reviewed-no-visuals)',
        {
          field: 'supportAids',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  // ── Answer leakage check ─────────────────────────────────────────────────

  if (
    Array.isArray(question.stimulusAids) &&
    question.stimulusAids.length > 0
  ) {
    const correctAnswer =
      question.questionType === 'multiple_choice' && question.options
        ? question.options[question.correctAnswer ?? -1]
        : undefined

    if (
      correctAnswer &&
      typeof correctAnswer === 'string' &&
      correctAnswer.trim()
    ) {
      const answerLower = correctAnswer.trim().toLowerCase()
      for (let i = 0; i < question.stimulusAids.length; i++) {
        const aid = question.stimulusAids[i]
        if (aid && typeof aid === 'object' && 'data' in aid) {
          const dataStr = JSON.stringify(aid.data).toLowerCase()
          if (
            dataStr.includes(`"label":"${answerLower}"`) ||
            dataStr.includes(`"value":"${answerLower}"`)
          ) {
            issues.push(
              coach(
                'visual',
                STAGE,
                'VISUAL_ANSWER_LEAKAGE',
                `Stimulus visual at index ${i} may leak the correct answer "${correctAnswer}"`,
                {
                  field: `stimulusAids[${i}]`,
                  questionId: qId,
                  familyId: fId,
                  severity: 60,
                  suggestion:
                    'Remove or modify this visual to not reveal the answer, or move it to supportAids',
                },
              ),
            )
          }
        }
      }
    }
  }

  return issues
}

// ─── Batch: Stimulus Consistency per Family ─────────────────────────────────

/**
 * Cross-question validation: checks that all questions within the same familyId
 * use the same stimulus visual type. Mixed stimulus types within a family
 * indicate inconsistent content authoring.
 *
 * Returns issues keyed to the first question that deviates from the majority type.
 */
export function runVisualConsistencyBatch(
  questions: Question[],
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  // Group questions by familyId
  const familyMap = new Map<string, Question[]>()
  for (const q of questions) {
    if (!q.familyId) continue
    const list = familyMap.get(q.familyId) ?? []
    list.push(q)
    familyMap.set(q.familyId, list)
  }

  for (const [familyId, familyQuestions] of familyMap) {
    // Collect stimulus types per question (only questions that HAVE stimulus aids)
    const typeCountMap = new Map<string, number>()
    const questionsByType = new Map<string, string[]>()

    for (const q of familyQuestions) {
      if (!Array.isArray(q.stimulusAids) || q.stimulusAids.length === 0)
        continue

      for (const aid of q.stimulusAids) {
        const aidObj = aid as Record<string, unknown> | undefined
        if (
          aidObj &&
          typeof aidObj === 'object' &&
          typeof aidObj.type === 'string'
        ) {
          const t = aidObj.type as string
          typeCountMap.set(t, (typeCountMap.get(t) ?? 0) + 1)
          const ids = questionsByType.get(t) ?? []
          ids.push(q.id ?? 'UNKNOWN')
          questionsByType.set(t, ids)
        }
      }
    }

    // If 0 or 1 distinct types → consistent, skip
    if (typeCountMap.size <= 1) continue

    // Find majority type
    let majorityType = ''
    let majorityCount = 0
    for (const [t, count] of typeCountMap) {
      if (count > majorityCount) {
        majorityType = t
        majorityCount = count
      }
    }

    // Flag minority types
    for (const [t, qIds] of questionsByType) {
      if (t === majorityType) continue
      for (const qId of qIds) {
        issues.push(
          coach(
            'visual',
            STAGE,
            'VISUAL_STIMULUS_INCONSISTENT_IN_FAMILY',
            `Family "${familyId}" mixes stimulus types: "${t}" (${qIds.length}×) vs majority "${majorityType}" (${majorityCount}×)`,
            {
              field: 'stimulusAids',
              expected: majorityType,
              actual: t,
              questionId: qId,
              familyId,
              severity: 40,
              suggestion: `Align stimulus type to "${majorityType}" for consistency, or remove the visual if it doesn't add value`,
            },
          ),
        )
      }
    }
  }

  return issues
}
